<template>
  <div>
    <app-header />

    <div class="layout-sidesheet row" style="height: calc(100vh - 75px);">

      <div class="col main-content">
        <div class="container my-5">
          <div class="page-heading m-b-lg">
            <div class="row">
              <div class="col-sm-6 col-lg-8">
                <h1>{{ sla.client ? sla.client.company : '' }}</h1>
                <p>{{ sla.type ? sla.type.name : '' }}</p>
              </div>
              <div class="col-sm-6 col-lg-4 text-end">
                <h3
                  class="title"
                  :class="sla.current_schedule && schedule.id === sla.current_schedule.id ? '' : 'text-primary'"
                >
                  {{ month }}
                </h3>
              </div>
            </div>
            <!-- /.row -->
          </div>
          <!-- /.page-heading -->

          <div role="alert" class="alert alert-danger" v-if="totalMismatch">
            <div class="alert-content">
              <p>
                <i class="fa fa-exclamation-triangle me-3 mt-1"></i>
                The time entries for this SLA months don't seem to match the total that was calculated at the end of the
                month
                <br />
                The time entries total is: {{ timeEntriesTotal }}h but at the end of the month we had {{ hoursUsed }}h
              </p>
            </div>
            <div class="alert-actions">
              <c-button variant="" @click="updateScheduleTotal">Update Total</c-button>
            </div>
          </div>

          <div role="alert" class="alert alert-danger" v-if="sla.client && sla.client.has_overdue_invoices">
            <i class="fa fa-exclamation-triangle me-3 mt-1"></i>
            Client has overdue invoices.
          </div>

          <div role="alert" class="alert alert-info" v-if="sla.client && sla.client.has_active_warranty">
            <i class="fa fa-exclamation-triangle me-3 mt-1"></i>
            Client currently has an active warranty.
          </div>

          <div role="alert" class="alert alert-info" v-if="sla.client && sla.client.has_active_evalution_period">
            <i class="fa fa-exclamation-triangle me-3 mt-1"></i>
            Client is currently in an evaluation period.
          </div>

          <div class="row mt-3">
            <div class="col-md-6 col-lg-4 mt-4">
              <div class="progress-radial">
                <svg width="150" height="150" viewBox="0 0 42 42" class="radial">
                  <circle cx="21" cy="21" r="15.91549430918954" fill="#fff" class="radial-hole"></circle>
                  <circle
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke="#dee2e6"
                    stroke-width="3"
                    class="radial-ring"
                  ></circle>
                  <circle
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke="#0360DC"
                    stroke-linecap="round"
                    stroke-width="3"
                    :stroke-dasharray="`${usagePercentage} ${100 - Math.min(usagePercentage, 100)}`"
                    stroke-dashoffset="25"
                    class="radial-segment"
                  ></circle>
                  <g class="progress-text">
                    <text x="50%" y="50%">{{ usagePercentage.toFixed() }}%</text>
                  </g>
                </svg>
                <ul class="steps">
                  <li class="previous"><a href="#previous">SLA Time Spent</a></li>
                  <li class="current">
                    {{ hoursUsed }}
                    <span class="suffix">Hrs</span>
                  </li>
                  <li class="next">of {{ totalHours }} Hrs</li>
                  <li class="next">{{ hoursRemaining }} Hrs remaining</li>
                </ul>
              </div>
            </div>
            <div class="col-md-6 col-lg-4 mt-4">
              <div class="progress-radial">
                <svg width="150" height="150" viewBox="0 0 42 42" class="radial">
                  <circle cx="21" cy="21" r="15.91549430918954" fill="#fff" class="radial-hole"></circle>
                  <circle
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke="#dee2e6"
                    stroke-width="3"
                    class="radial-ring"
                  ></circle>
                  <circle
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke="#0360DC"
                    stroke-linecap="round"
                    stroke-width="3"
                    :stroke-dasharray="`${this.daysRemainingPercentage} ${
                      100 - Math.min(this.daysRemainingPercentage, 100)
                    }`"
                    stroke-dashoffset="25"
                    class="radial-segment"
                  ></circle>
                  <g class="progress-text">
                    <text x="50%" y="50%">{{ daysRemainingPercentage.toFixed() }}%</text>
                  </g>
                </svg>
                <ul class="steps">
                  <li class="previous"><a href="#previous">Days Remaining</a></li>
                  <li class="current">{{ daysRemaining }} Days</li>
                  <li class="next">
                    <a href="#next">
                      {{ monthMoment.startOf('month') | moment('D/M/YYYY') }} -
                      {{ monthMoment.endOf('month') | moment('D/M/YYYY') }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="col-md-12 col-lg-4">
              <div>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="card card-widget">
                      <div class="card-body">
                        <h3 class="widget-title">Warranty Hours</h3>
                        <p class="lead strong">
                          {{ warrantyHours }}
                          <small>hrs</small>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="card card-widget">
                      <div class="card-body">
                        <h3 class="widget-title">Service Value</h3>
                        <p class="lead strong">
                          {{ serviceValueHours }}
                          <small>hrs</small>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="card card-widget">
                      <div class="card-body">
                        <h3 class="widget-title">Billable Hours</h3>
                        <p class="lead strong">
                          {{ billableHours }}
                          <small>hrs</small>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="card card-widget">
                      <div class="card-body">
                        <h3 class="widget-title">Monthly Hours</h3>
                        <p class="lead strong">
                          {{ schedule.monthly_hours }}
                          <small>hrs</small>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- /.row -->
              </div>
              <!-- /.widget-overview-data -->
            </div>
          </div>

          <hr />

          <sla-ticket-breakdown :start="startDate" :end="endDate" :sla-id="sla.id" />

          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-md-12 col-lg-12">
                  <div class="ibox">
                    <div class="ibox-title">
                      <div class="row">
                        <div class="col-xl-10">
                          <h2>SLA Overview</h2>
                        </div>
                        <div class="col-xl-2 text-right">
                          <button type="button" class="btn btn-link" v-c-modal:modal="{}">
                            <i class="fal fa-pen"></i>
                          </button>
                        </div>
                      </div>
                      <!-- /.row -->
                    </div>
                    <div class="ibox-content">
                      <div class="">
                        <div class="row row-clear-md-6-lg-4">
                          <div class="col-md-6 col-lg-4">
                            <div class="form-group form-group-material">
                              <label class="control-label">Client</label>
                              <p class="form-control-static">
                                {{ sla.client ? sla.client.company : '' }}
                              </p>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-4">
                            <div class="form-group form-group-material">
                              <label class="control-label">Type</label>
                              <p class="form-control-static">
                                {{ sla.type ? sla.type.name : '' }}
                              </p>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-4">
                            <div class="form-group form-group-material">
                              <label class="control-label">SLA Range</label>
                              <p class="form-control-static">
                                {{ sla.start_date | moment('D MMM YYYY') }} -
                                {{ sla.end_date | moment('D MMM YYYY') }}
                              </p>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-4">
                            <div class="form-group form-group-material">
                              <label class="control-label">Override Rate</label>
                              <p class="form-control-static" v-show="sla.override_rate">
                                ${{ sla.override_rate }} per hour
                              </p>
                              <p class="form-control-static" v-show="!sla.override_rate">Using base</p>
                            </div>
                          </div>
                        </div>

                        <div class="row">
                          <div class="col-3 col-md-4 col-lg-4">
                            <p>
                              <strong>Notification 50% Sent:</strong>
                              {{ schedule.notification_50 ? schedule.notification_50 : '-' }}
                            </p>
                            <p v-if="schedule.notification_50 || schedule.notification_70">
                              <strong>Notification 70% Sent:</strong>
                              {{ schedule.notification_70 ? schedule.notification_70 : '-' }}
                            </p>
                            <p v-if="schedule.notification_70 || schedule.notification_100">
                              <strong>Notification 100% Sent:</strong>
                              {{ schedule.notification_100 ? schedule.notification_100 : '-' }}
                            </p>
                          </div>
                          <div class="col-3 col-md-4 col-lg-4">
                            <strong>SLA Usage Notifications:</strong>
                            <p v-for="contact in notifiableContacts" :key="contact.id">
                              {{ contact.email }} ({{ contact.name }})
                            </p>
                          </div>
                        </div>
                        <!-- /.row -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <h2>Tickets</h2>
            </div>
            <div class="col-md-6 text-end">
              <c-button @click="updateTickets" variant="danger">Sync Freshdesk Tickets</c-button>
            </div>
          </div>
          <c-data-list ref="dataList" :items="tickets">
            <template v-slot:columns>
              <div class="col-1"></div>
              <div class="col-1">Ticket #</div>
              <div class="col-4">Subject</div>
              <div class="col-1">Created</div>
              <div class="col-1">Hrs</div>
              <div class="col-3">Status</div>
            </template>
            <template v-slot:default="slotProps">
              <a>
                <div class="row">
                  <div class="col-1">
                    <c-avatar
                      :nam="slotProps.item.user ? slotProps.item.user.name : ''"
                      :url="getAvatar(slotProps.item.user_id)"
                    />
                  </div>
                  <div class="col-1">
                    <p class="ticket-id">
                      <a
                        target="_blank"
                        v-bind:href="'https://support.codium.com.au/helpdesk/tickets/' + slotProps.item.freshdesk_ticket_id"
                      >
                        #{{ slotProps.item.freshdesk_ticket_id }}
                      </a>
                    </p>
                  </div>
                  <div class="col-4">
                    <div v-if="slotProps.item.rate_type_id === 2" class="badge bg-danger me-1">Urgent</div>
                    <p :title="slotProps.item.subject" class="truncate">
                      {{ slotProps.item.subject }}
                    </p>
                  </div>
                  <div class="col-1">
                    <p class="truncate">
                      {{ slotProps.item.created_at | moment('DD/MM/YYYY') }}
                    </p>
                  </div>
                  <div class="col-1">
                    <p class="truncate">{{ sumTimeEntries(slotProps.item.time_entries) }} hrs</p>
                  </div>
                  <div class="col-3">
                    <div v-if="slotProps.item.paid_work ? slotProps.item.paid_work.toLowerCase().includes('paid') : ''" class="badge bg-primary me-1">
                      PAID
                    </div>
                    <div v-if="slotProps.item.paid_work ? slotProps.item.paid_work === 'Covered under warranty' : ''" class="badge bg-warning me-1">
                      Warranty
                    </div>
                    <div v-if="slotProps.item.paid_work ? slotProps.item.paid_work === 'Service value' : ''" class="badge bg-info me-1">
                      Service Value
                    </div>
                    <div v-else class="badge bg-secondary me-1">
                      {{ slotProps.item.ticket_type }}
                    </div>
                    <div :class="'truncate me-1 badge bg-' + ([4, 5].includes(slotProps.item.status) ? 'dark' : 'success')">
                      <p class="truncate">{{ slotProps.item.status_title }}</p>
                    </div>
                  </div>
                  <div class="col-1 text-end">
                    <button
                      :aria-controls="'collapse-' + slotProps.item.id"
                      aria-expanded="false"
                      class="btn collapsed"
                      :data-target="'#collapse-' + slotProps.item.id"
                      data-toggle="collapse"
                      type="button"
                    >
                      <i class="fa fa-plus"></i>
                    </button>
                  </div>
                </div>
                <!-- /.row -->

                <div class="ticket-detail collapse" :id="'collapse-' + slotProps.item.id">
                  <div class="inner">
                    <div class="row">
                      <div class="col-sm-6 col-md-4">
                        <p>
                          <strong>Type:</strong>
                          {{ slotProps.item.rate_type.rate_name }}
                        </p>
                      </div>
                      <div class="col-sm-6 col-md-4">
                        <p v-if="slotProps.item.user">
                          <strong>Developer:</strong>
                          {{ slotProps.item.user.name }}
                        </p>
                        <p v-else>
                          <strong>Developer:</strong>
                          Unkown
                        </p>
                      </div>
                      <div class="col-sm-6 col-md-4">
                        <p>
                          <strong>Closing Reason:</strong>
                          {{ slotProps.item.closing_reason }}
                        </p>
                      </div>
                    </div>
                    <!-- /.row -->

                    <p class="strong">Time Entries:</p>
                    <ul class="time-entries">
                      <li v-for="timeEntry in slotProps.item.time_entries" :key="timeEntry.id" class="row">
                        <div class="col-6 col-md-3 col-lg-2">
                          {{ timeEntry.entry_date | moment('DD/MM/YYYY') }}
                        </div>
                        <div class="col-6 col-md-3 col-lg-3">
                          <p v-if="timeEntry.user" class="truncate">
                            {{ timeEntry.user.name }}
                          </p>
                          <p v-else class="truncate">Unknown</p>
                        </div>
                        <div class="col-6 col-md-3 col-lg-2">{{ timeEntry.spent }} hrs</div>
                        <div class="col-6 col-md-3 col-lg-2">
                          <p>&nbsp;</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <!-- /.collapse -->
              </a>
            </template>
          </c-data-list>

          <h2>Client Notes</h2>

          <div class="card">
            <div class="card-body">
              <wysiwyg-inline
              v-if="loaded"
              :content="clientNotes"
              :save="saveDescription"
              ></wysiwyg-inline>

              <small>*These will be shown to the client on the SLA report.</small>
            </div>
            </div>

          <div class="d-flex align-items-center gap-2 mt-0">
            <button type="button" @click="downloadReport" class="btn btn-primary">
              <i class="fa fa-file-pdf-o me-1"></i>
              Export Report PDF
            </button>
            <button type="button" @click="confirmSendReport" class="btn btn-danger">
              <i class="fa fa-paper-plane me-1"></i>
              Send Report PDF
            </button>
            <button
              v-if="schedule.report_sends && schedule.report_sends.length"
              type="button"
              class="btn btn-link btn-sm text-muted ps-0"
              v-c-modal:reportHistoryModal="{}"
            >
              <i class="fa fa-history me-1"></i>
              Send history
            </button>
          </div>
        </div>
      </div>

      <notes v-if="loaded"></notes>
    </div>

    <sla-report-history ref="reportHistoryModal" :sends="schedule.report_sends || []" />

    <sla-form ref="modal" title="Edit SLA" :sla="sla" @updated="onSlaUpdated"></sla-form>

    <complete-sla ref="completeModal" :sla="sla" @completed="onSlaCompleted"></complete-sla>
  </div>
</template>

<script>
import Vue from 'vue'
import { map, filter, sumBy } from 'lodash'
import SlaForm from 'components/slas/SlaForm'
import CompleteSla from 'components/slas/CompleteSla'
import SlaTicketBreakdown from 'components/misc/SlaTicketBreakdown'
import $ from 'jquery'
import { SET_SLA } from 'vx/mutations'
import { mapMutations, mapActions, mapState } from 'vuex'
import { LaddaButton } from 'components/form/input'
import { notify, http } from 'utils'
import PercentageGraph from 'components/misc/PercentageGraph'
import auth from 'utils/auth'
import { user } from 'mixins'
import Notes from 'components/notes/Notes.vue'
import { WysiwygInline } from "components/form/input";
import SlaReportHistory from 'components/slas/SlaReportHistory'

export default {
  mixins: [user],

  components: {
    SlaForm,
    LaddaButton,
    PercentageGraph,
    CompleteSla,
    SlaTicketBreakdown,
    Notes,
    WysiwygInline,
    SlaReportHistory,
  },

  data: function () {
    return {
      loaded: false,
      loadingBreakdown: false,
      breakdown: null,
      tickets: [],
      timeEntriesTotal: 0,
      clientNotes: '',
    }
  },

  async created () {
    await this.getSLA({ id: this.$route.params.sla_id, payload: '' })
    this.getTimeEntries()
    this.setNotesStateProperty({ property: 'parent', value: 'sla' })
    this.clientNotes = this.schedule.client_notes
    this.loaded = true
  },

  computed: {
    ...mapState('sla', ['sla']),
    ...mapState('user', ['authorized']),

    daysRemaining() {
      return Math.max(this.monthMoment.endOf('month').diff(moment(), 'days'), 0)
    },

    daysRemainingPercentage() {
      return Math.max(100 - (this.daysRemaining / this.monthMoment.daysInMonth()) * 100, 0)
    },

    hoursUsed() {
      var used = parseFloat(this.schedule.monthly_hours) - parseFloat(this.schedule.remaining)

      return used.toFixed(2)
    },

    hoursRemaining() {
      return Math.max(this.totalHours - this.hoursUsed, 0).toFixed(2)
    },

    usagePercentage() {
      return parseFloat(((this.hoursUsed / this.totalHours) * 100).toFixed(0))
    },

    totalHours() {
      return (parseFloat(this.schedule.monthly_hours) + Math.max(parseFloat(this.schedule.rollover), 0)).toFixed(2)
    },

    schedule: function () {
      var that = this
      var scheduleItem = this.sla.schedule.find(function (schedule) {
        return schedule.id == that.$route.query.scheduleId
      })

      if (scheduleItem === undefined) {
        return this.sla.current_schedule
      }

      return scheduleItem
    },

    month() {
      var startDate = moment(this.sla.start_date)

      if (this.schedule.month === 1) {
        return startDate.format('MMMM')
      } else {
        var scheduleDate = startDate.date(1).add(this.schedule.month - 1, 'months')

        return scheduleDate.format('MMMM')
      }
    },

    startDate() {
      return this.monthMoment.clone().startOf('month')
    },

    endDate() {
      return this.monthMoment.clone().endOf('month')
    },

    monthMoment() {
      if (!this.sla.start_date) {
        return moment()
      }

      var startDate = moment(this.sla.start_date)

      if (this.schedule.month === 1) {
        return startDate
      } else {
        var scheduleDate = startDate.date(1).add(this.schedule.month - 1, 'months')
        return scheduleDate
      }
    },

    totalMismatch() {
      if (!this.loaded) {
        return false
      }

      return this.timeEntriesTotal !== parseFloat(this.hoursUsed)
    },

    warrantyHours() {
      var hours = 0

      map(this.tickets, (ticket) => {
        if (ticket.paid_work == 'Covered under warranty') {
          hours += sumBy(ticket.time_entries, 'spent')
        }
      })

      return hours.toFixed(2)
    },

    serviceValueHours() {
      var hours = 0

      map(this.tickets, (ticket) => {
        if (ticket.paid_work == 'Service value') {
          hours += sumBy(ticket.time_entries, 'spent')
        }
      })

      return hours.toFixed(2)
    },

    billableHours() {
      var hours = 0

      map(this.tickets, (ticket) => {
        if (ticket.paid_work == 'Paid at standard rate' || ticket.paid_work == 'Paid at urgent rate') {
          hours += sumBy(ticket.time_entries, 'spent')
        }
      })

      return hours.toFixed(2)
    },

    openTickets() {
      var openTickets = filter(this.tickets, (item) => {
        return item.status_title === 'open'
      })

      return openTickets.length
    },

    notifiableContacts() {
      if (this.sla.client === undefined) return

      return this.sla.client.contacts.filter((contact) => {
        return contact.sla_notifications === 1
      })
    },
  },
  methods: {
    ...mapMutations('sla', [SET_SLA]),
    ...mapActions('sla', ['getSLA', 'updateSLATimesMistmatch', 'completeSLA', 'sendUsageReport']),
    ...mapActions('notes', {setNotesStateProperty: 'setStateProperty'}),

    loadSilencer(done) {
      done(true)
    },

    onSlaUpdated() {
      this.getSLA({ id: this.$route.params.sla_id, payload: '' })
    },

    onSlaCompleted() {
      if (this.$refs.modal) this.$refs.modal.close()
      this.getSLA({ id: this.$route.params.sla_id, payload: '' })
    },
    getTimeEntries() {
      var that = this

      Promise.all([
        client({
          path: '/slas/' + this.$route.params.sla_id + '/times?scheduleId=' + this.$route.query.scheduleId,
        }).then(
          function (response) {
            return response.entity
          },
          function (response) {}
        ),
      ])
        .then(function (data) {
          that.tickets = data[0].tickets
          that.timeEntriesTotal = data[0].total
        })
        .finally(() => {
          this.loaded = true
        })
    },

    sumTimeEntries(timeEntries) {
      var total = 0

      for (var i = 0; i < timeEntries.length; i++) {
        total += timeEntries[i].spent
      }

      return Math.round(total * 100) / 100
    },

    classForPercentage: function (percentage, prepend) {
      if (percentage < 30) {
        return prepend + '-good'
      } else if (percentage < 70) {
        return prepend + '-low'
      } else {
        return prepend + '-danger'
      }
    },

    daysTo(dateString) {
      var now = moment()
      return moment(dateString).diff(now, 'days')
    },

    updateScheduleTotal() {
      return this.updateSLATimesMistmatch({
        id: this.$route.params.sla_id,
        payload: { scheduleId: this.$route.query.scheduleId },
      })
        .then((response) => {
          this.tickets = response.data.tickets
          this.timeEntriesTotal = response.data.total
        })
        .catch((error) => {
          notify.error('Sorry something went wrong trying to update the total, please try again.')
        })
    },

    downloadReport() {
      var link =
        process.env.VUE_APP_API_URL +
        '/slas/' +
        this.sla.id +
        '/schedules/' +
        this.schedule.id +
        '/usage-report?token=' +
        auth.getToken()
      window.open(link)
    },

    confirmSendReport() {
      const emails = this.notifiableContacts
        .map((contact, index) => {
          return contact.email
        })
        .join(', ')

      this.$swal({
        title: 'Confirm Send',
        html: 'Are you sure you want to send the report?<br/>It will be sent to <b>' + emails + '</b>.',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Send',
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return this.handleSendReport()
            .then(() => {
              notify.success('Email was successfully sent.')
            })
            .catch((error) => {
              // this.$swal.hideLoading()
              this.$swal.close()

              if (error.response.status.code == 422) {
                notify.error('Sorry there was no email for the contact on this sla.')
              } else {
                notify.error('Sorry there was an issue trying to send the email, please try again.')
              }
            })
        },
      })
    },

    handleSendReport() {
      return this.sendUsageReport({
        slaId: this.sla.id,
        scheduleId: this.schedule.id,
      }).then(() => {
        return this.getSLA({ id: this.$route.params.sla_id, payload: '' })
      })
    },

    updateTickets() {
      return http
        .post(`slas/${this.$route.params.sla_id}/schedules/${this.$route.query.scheduleId}/sync-tickets`)
        .then((response) => {
          this.getTimeEntries()
          notify.success('Successfully synced Tickets.')
        })
        .catch(() => {
          notify.error('Sorry something went wrong trying to sync the tickets, probably dont bother trying again.')
        })
    },

    saveDescription (data) {
      return new Promise(() => {
          http.put(`slas/${this.$route.params.sla_id}/schedules/${this.$route.query.scheduleId}`, { client_notes: data })
      })
    }
  },
}
</script>

